import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import api from "../../services/api";
import moment from "moment-timezone";
import { getCurrentUser } from "../../common/utils";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

/**
 * ModalPersonalizadoServicos - Modal reutilizável para formulários dinâmicos de serviços.
 * 
 * Lê a configuração de `formulario_dinamico_servicos_procedimentos` do serviço e renderiza
 * campos dinâmicos. Suporta controle de visibilidade por perfil (control-show.deny-profiles).
 * 
 * Ao submeter:
 * - PUT no agendamento com dados_formulario_dinamico_servicos
 * - POST em /atendimentos/relatos/ para registrar o relato na etapa atual
 */
const ModalPersonalizadoServicos = ({
    show,
    onHide,
    agendamento = null,
    contextData = null,
    onSuccess,
    readOnly = false,
}) => {
    // Form state
    const [camposDinamicosValores, setCamposDinamicosValores] = useState({});
    const [camposDinamicosArquivos, setCamposDinamicosArquivos] = useState({});
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Computed values
    // Computed values - parsing configurations with useMemo
    const { configuracoes, camposConfig, tituloBotao } = useMemo(() => {
        let configs = agendamento?.dados_servico_procedimento?.configuracoes;
        
        if (typeof configs === 'string') {
            try {
                configs = JSON.parse(configs);
            } catch (e) {
                console.error("Erro ao fazer parse de configuracoes no modal:", e);
                configs = {};
            }
        }
        
        const configFormulario = configs?.formulario_dinamico_servicos_procedimentos;
        return {
            configuracoes: configs,
            camposConfig: configFormulario?.campos || [],
            tituloBotao: configFormulario?.titulo_botao || 'Formulário'
        };
    }, [agendamento]);

    const podeVisualizarCampo = (campo) => {
        // ... implementation (unchanged logic, assuming it's closed over correctly or moved)
        if (!readOnly) return true;
        if (!campo['control-show']) return true;

        const controlShow = campo['control-show'];
        const dados_usuario = getCurrentUser();
        const tipoUsuario = dados_usuario?.tipo_usuario || '';

        if (controlShow['allow-profiles'] && Array.isArray(controlShow['allow-profiles'])) {
            return controlShow['allow-profiles'].includes(tipoUsuario);
        }

        if (controlShow['deny-profiles'] && Array.isArray(controlShow['deny-profiles'])) {
            return !controlShow['deny-profiles'].includes(tipoUsuario);
        }

        return true;
    };

    const camposVisiveis = camposConfig.filter(podeVisualizarCampo);

    // Debug logs removidos
    useEffect(() => {
        // Nada a fazer aqui por enquanto
    }, [show, agendamento, configuracoes, camposConfig]);

    // Todos os campos são visíveis para preenchimento
    // O control-show.deny-profiles é usado apenas na visualização das respostas no histórico de etapas
    // const camposVisiveis = camposConfig;

    useEffect(() => {
        if (show && agendamento) {
            let respostasExistentes = agendamento.dados_formulario_dinamico_servicos || {};
            
            if (Array.isArray(respostasExistentes)) {
                const respostasObj = {};
                respostasExistentes.forEach(item => {
                    respostasObj[item.slug] = item.value;
                });
                respostasExistentes = respostasObj;
            }

            setCamposDinamicosValores(respostasExistentes);
            setCamposDinamicosArquivos({});
            setError(null);
        }
    }, [show, agendamento]);

    const handleDynamicFieldChange = useCallback((slug, value) => {
        if (readOnly) return;
        
        setCamposDinamicosValores(prev => {
            const novosValores = { ...prev, [slug]: value };

            // Encontra a configuração do campo atual
            const campoConfig = camposConfig.find(c => c.slug === slug);
            
            if (campoConfig) {
                if (campoConfig.calculo_data_automatica && value) {
                    const { campo_destino, dias_para_adicionar } = campoConfig.calculo_data_automatica;
                    const diasAdd = parseInt(dias_para_adicionar);

                    if (campo_destino && !isNaN(diasAdd)) {
                        const dataBase = moment(value, "YYYY-MM-DD", true); // Strict parsing
                        
                        if (dataBase.isValid()) {
                            const novaData = dataBase.clone().add(diasAdd, 'days');
                            
                            // Ajuste fim de semana
                            if (novaData.day() === 6) { // Sábado -> Segunda
                                novaData.add(2, 'days');
                            } else if (novaData.day() === 0) { // Domingo -> Segunda
                                novaData.add(1, 'days');
                            }

                            const dataFormatada = novaData.format('YYYY-MM-DD');
                            novosValores[campo_destino] = dataFormatada;
                        }
                    } 
                }
            }

            return novosValores;
        });
    }, [readOnly, camposConfig]);

    const handleDynamicFileChange = useCallback((slug, file) => {
        if (readOnly) return;
        setCamposDinamicosArquivos(prev => ({ ...prev, [slug]: file }));
    }, [readOnly]);

    const validate = () => {
        for (const campo of camposVisiveis) {
            if (!campo.obrigatorio) continue;

            if (campo.tipo === "file") {
                if (!camposDinamicosArquivos[campo.slug] && !camposDinamicosValores[campo.slug]) { // Check existing value for file too
                    return `Por favor, selecione um arquivo para "${campo.label}"`;
                }
            } else if (campo.tipo === "radio") {
                if (!camposDinamicosValores[campo.slug]) {
                    return `Por favor, selecione uma opção para "${campo.label}"`;
                }
            } else {
                if (!camposDinamicosValores[campo.slug]?.trim()) {
                    return `Por favor, preencha o campo "${campo.label}"`;
                }
            }
        }

        return null;
    };

    const handleSubmit = async () => {
        if (readOnly) return;

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const dataAtual = moment().tz("America/Sao_Paulo").format("YYYY-MM-DD HH:mm:ss");

            const dadosDinamicosArray = [];
            
            camposVisiveis.forEach(campo => {
                const valor = camposDinamicosValores[campo.slug];
                if (valor !== undefined && valor !== null && valor !== "") {
                    dadosDinamicosArray.push({
                        slug: campo.slug,
                        label: campo.label,
                        value: valor,
                        tipo: campo.tipo
                    });
                }
            });

            const payload = {
                dados_formulario_dinamico_servicos: dadosDinamicosArray,
            };

            const hasFiles = Object.keys(camposDinamicosArquivos).length > 0;
            let requestPromise;

            if (hasFiles) {
                const formData = new FormData();
                formData.append('dados_formulario_dinamico_servicos', JSON.stringify(camposDinamicosValores));

                Object.entries(camposDinamicosArquivos).forEach(([slug, arquivo]) => {
                    formData.append(`arquivo_${slug}`, arquivo);
                });

                requestPromise = api.put(`/agendamentos/${agendamento.id}/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                requestPromise = api.put(`/agendamentos/${agendamento.id}/`, payload);
            }

            await requestPromise;

            const etapaId = contextData?.etapa_atual?.id || agendamento.etapa_atual?.id;
            const atendimentoId = contextData?.atendimento_id || agendamento.atendimento_id;
            
            if (etapaId && atendimentoId) {
                const relatoFormData = new FormData();
                relatoFormData.append("data", dataAtual);
                relatoFormData.append("etapa_id", etapaId);
                relatoFormData.append("atendimento_id", atendimentoId);
                relatoFormData.append("tipo", "etapa");
                relatoFormData.append("titulo", `Preenchimento: ${tituloBotao}`);
                relatoFormData.append("servico_procedimento_id", agendamento.servico_procedimento_id);
                relatoFormData.append("ignorar_cadastro_servico", true);
                relatoFormData.append("agendamento_id", agendamento.id);

                await api.post("/atendimentos/relatos/", relatoFormData);
            }

            const MySwal = withReactContent(Swal);
            await MySwal.fire({
                title: 'Sucesso!',
                text: 'Dados salvos com sucesso.',
                icon: 'success',
                confirmButtonColor: '#3085d6',
            });

            onSuccess();
            onHide();
        } catch (err) {
            console.error("Erro ao salvar formulário dinâmico:", err);
            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.detail ||
                "Erro ao salvar os dados. Tente novamente.";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Render dynamic fields
    const renderDynamicFields = () => {
        if (camposVisiveis.length === 0) {
            if (readOnly && camposConfig.length > 0) {
                return <p className="text-muted">Respostas não disponíveis para esse perfil</p>;
            }
            return <p className="text-muted">Nenhum campo configurado para este formulário.</p>;
        }

        return camposVisiveis.map((campo) => {
            const requiredMark = (!readOnly && campo.obrigatorio) ? <span className="text-danger">*</span> : null;

            switch (campo.tipo) {
                case "date":
                    return (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>{campo.label} {requiredMark}</Form.Label>
                            <Form.Control
                                type="date"
                                disabled={readOnly}
                                value={camposDinamicosValores[campo.slug] || ""}
                                onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                            />
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}
                        </Form.Group>
                    );

                case "file":
                    return (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>{campo.label} {requiredMark}</Form.Label>
                            {!readOnly && (
                                <Form.Control
                                    type="file"
                                    accept={campo.extensoes || ".pdf,.jpg,.jpeg,.png"}
                                    onChange={(e) => handleDynamicFileChange(campo.slug, e.target.files[0])}
                                />
                            )}
                            {readOnly && camposDinamicosValores[campo.slug] && (
                                <div className="mt-2">
                                     <a href={camposDinamicosValores[campo.slug]} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                         Ver Arquivo Atual
                                     </a>
                                </div>
                            )}
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}
                        </Form.Group>
                    );

                case "radio":
                    return (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>{campo.label} {requiredMark}</Form.Label>
                            <div>
                                {(campo.opcoes || []).map((opcao, idx) => {
                                    const descricao = campo.descricoes_opcoes?.[opcao];
                                    return (
                                        <div key={idx}>
                                            <Form.Check
                                                type="radio"
                                                id={`${campo.slug}-${idx}`}
                                                name={campo.slug}
                                                label={opcao}
                                                value={opcao}
                                                disabled={readOnly}
                                                checked={camposDinamicosValores[campo.slug] === opcao}
                                                onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                                            />
                                            {descricao && (
                                                <Form.Text className="text-muted d-block ms-4 mb-2">
                                                    {descricao}
                                                </Form.Text>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}
                        </Form.Group>
                    );

                case "textarea":
                    return (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>{campo.label} {requiredMark}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                disabled={readOnly}
                                placeholder={readOnly ? "" : (campo.placeholder || "")}
                                value={camposDinamicosValores[campo.slug] || ""}
                                onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                            />
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}
                        </Form.Group>
                    );

                case "text":
                default:
                    return (
                        <Form.Group className="mb-3" key={campo.slug}>
                            <Form.Label>{campo.label} {requiredMark}</Form.Label>
                            <Form.Control
                                type="text"
                                disabled={readOnly}
                                placeholder={readOnly ? "" : (campo.placeholder || "")}
                                value={camposDinamicosValores[campo.slug] || ""}
                                onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                            />
                            {campo.observacao && (
                                <Form.Text className="text-muted">{campo.observacao}</Form.Text>
                            )}
                        </Form.Group>
                    );
            }
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{tituloBotao} {readOnly ? '(Visualização)' : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                <p>
                    {readOnly ? "Respostas enviadas para " : "Preencha os dados para "}
                    <strong>{agendamento?.dados_servico_procedimento?.nome}</strong>
                </p>

                {renderDynamicFields()}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isLoading}>
                    Fechar
                </Button>
                {!readOnly && (
                    <Button variant="success" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Salvando...
                            </>
                        ) : (
                            "Salvar"
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

ModalPersonalizadoServicos.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    agendamento: PropTypes.shape({
        id: PropTypes.number,
        servico_procedimento_id: PropTypes.number,
        atendimento_id: PropTypes.number,
        dados_servico_procedimento: PropTypes.shape({
            nome: PropTypes.string,
            configuracoes: PropTypes.object,
        }),
        etapa_atual: PropTypes.shape({
            id: PropTypes.number,
        }),
    }),
    contextData: PropTypes.shape({
        atendimento_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        etapa_atual: PropTypes.shape({
            id: PropTypes.number,
        }),
    }),
    onSuccess: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default ModalPersonalizadoServicos;
